import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, CircularProgress, IconButton, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AppShell from '../components/AppShell';
import api from '../services/api';
import { stripTags } from './conditions';
import { wranglerError } from './errors';
import useWranglerJob from './useWranglerJob';

const MASK = '••••••••••••••••';

const CopyButton = ({ value, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked: the value is on screen to copy by hand (except the masked secret).
    }
  };
  return (
    <Tooltip title={copied ? 'Copied' : label}>
      <IconButton size="small" onClick={copy} aria-label={label}>
        <ContentCopyRoundedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
};

const Value = ({ children, copy }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Box component="code" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 1 }}>
      {children}
    </Box>
    {copy != null && <CopyButton value={copy} />}
  </Box>
);

const Setting = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap', mb: 0.75 }}>
    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 190 }}>{label}</Typography>
    <Box sx={{ typography: 'body2' }}>{children}</Box>
  </Box>
);

const Step = ({ number, title, children }) => (
  <Paper component="section" aria-label={`${number}. ${title}`} variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
    <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={600} sx={{ mb: 1.5 }}>
      {number}. {title}
    </Typography>
    {children}
  </Paper>
);

const yesNo = (value) => (value ? 'On' : 'Off');

const Header = ({ header }) => {
  const [revealed, setRevealed] = useState(false);
  if (!header.value) {
    return (
      <>
        <Setting label="Header name"><Value copy={header.name}>{header.name}</Value></Setting>
        <Alert severity="error">
          Wrangler has no webhook secret (<code>WEBHOOK_SECRET</code> in its environment is empty), so
          there's no header value to enter. Until it's set, Wrangler rejects every webhook. Production
          Wrangler won't start without one, so this only happens on a development Wrangler.
        </Alert>
      </>
    );
  }
  return (
    <>
      <Setting label="Header name"><Value copy={header.name}>{header.name}</Value></Setting>
      <Setting label="Header value">
        <Value copy={header.value}>{revealed ? header.value : MASK}</Value>
        <Button size="small" onClick={() => setRevealed(!revealed)} sx={{ textTransform: 'none', ml: 1 }}>
          {revealed ? 'Hide' : 'Reveal'}
        </Button>
      </Setting>
      <Typography variant="body2" color="textSecondary">
        This is the webhook secret. Anyone who has it can send Wrangler fake completes for any survey.
      </Typography>
    </>
  );
};

const CustomFields = ({ fields }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>Variable name</TableCell>
        <TableCell>Question</TableCell>
        <TableCell>Type</TableCell>
        <TableCell>Sent as</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {fields.map((field) => (
        <TableRow key={field.variable_name}>
          <TableCell><Value copy={field.variable_name}>{field.variable_name}</Value></TableCell>
          <TableCell>
            {field.question_id != null && <Typography variant="caption" color="textSecondary">Q{field.question_id} </Typography>}
            {stripTags(field.title)}
          </TableCell>
          <TableCell>{field.type}</TableCell>
          <TableCell><code>{field.sent_as}</code></TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

/**
 * What a webhook sends: the secret header from step 2, then its custom fields. "Post custom
 * fields" is Alchemer's own label, even on the GET check.
 */
const Sends = ({ heading, header, fieldsLabel, children }) => (
  <>
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.75 }}>{heading}</Typography>
    <Setting label="Custom header">
      <Value copy={header.name}>{header.name}</Value>, with the value from step 2
    </Setting>
    <Setting label="Post custom fields">{fieldsLabel}</Setting>
    {children}
  </>
);

const Webhook = ({ webhook, children }) => (
  <>
    <Setting label="Method"><Value>{webhook.method}</Value></Setting>
    <Setting label="URL"><Value copy={webhook.url}>{webhook.url}</Value></Setting>
    <Setting label="Where">{webhook.placement}</Setting>
    {webhook.run && <Setting label="Run">{webhook.run}</Setting>}
    <Setting label="Asynchronous Connect">{yesNo(webhook.asynchronous_connect)}</Setting>
    {children}
  </>
);

/** 409 from Wrangler: questions never synced, or quotas use questions that are gone. */
const NotReady = ({ surveyId, problem, onSynced }) => {
  const [jobId, setJobId] = useState(null);
  const [error, setError] = useState(null);
  const { job, error: jobError } = useWranglerJob(surveyId, jobId);

  useEffect(() => {
    if (job?.status === 'done') onSynced();
  }, [job?.status, onSynced]);

  const sync = async () => {
    setError(null);
    try {
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/sync-questions/`);
      setJobId(data.job.id);
    } catch (err) {
      setError(wranglerError(err));
    }
  };

  const running = Boolean(jobId) && !jobError && !['done', 'failed'].includes(job?.status);
  const missing = problem.code === 'questions_missing';
  return (
    <Alert
      severity="warning"
      action={(
        <Button color="inherit" size="small" disabled={running} onClick={sync} sx={{ textTransform: 'none' }}>
          {running ? 'Syncing…' : 'Sync questions'}
        </Button>
      )}
    >
      <strong>{missing ? 'Some questions are missing' : 'Sync questions first'}</strong>
      <Box>{problem.detail}</Box>
      {missing && problem.question_ids?.length > 0 && <Box>Question IDs: {problem.question_ids.join(', ')}</Box>}
      {job?.status === 'failed' && <Box>The sync failed: {job.error || 'no reason given.'}</Box>}
      {(error || jobError) && <Box>{error || jobError}</Box>}
    </Alert>
  );
};

/**
 * The exact Alchemer setup for a survey using Wrangler (Wrangler's DESIGN.md
 * sections 8.1 and 11). Stack only: it contains the webhook secret, which is
 * held in this page's state only and masked until revealed.
 */
const WranglerSetup = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [state, setState] = useState({ loading: true, config: null, problem: null, error: null });

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => setSurvey(res.data)).catch(() => {});
  }, [surveyId]);

  const load = useCallback(async () => {
    setState({ loading: true, config: null, problem: null, error: null });
    try {
      const { data } = await api.get(`/surveys/${surveyId}/wrangler/webhook-config/`);
      setState({ loading: false, config: data, problem: null, error: null });
    } catch (err) {
      if (err?.response?.status === 409) {
        setState({ loading: false, config: null, problem: err.response.data, error: null });
      } else if (err?.response?.status === 404) {
        setState({ loading: false, config: null, problem: null,
          error: "This survey isn't registered in Wrangler. Register it from the survey page first." });
      } else {
        setState({ loading: false, config: null, problem: null, error: wranglerError(err) });
      }
    }
  }, [surveyId]);

  useEffect(() => { load(); }, [load]);

  const { config } = state;

  return (
    <AppShell>
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        {survey && (
          <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>Project</Link>
        )}
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          {survey ? survey.name : 'Survey'}
        </Link>
        <Link to={`/survey/${surveyId}/wrangler?tab=setup`} style={{ textDecoration: 'none', color: 'inherit' }}>Wrangler</Link>
        <Typography color="textPrimary">Alchemer webhook setup</Typography>
      </Breadcrumbs>

      <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700} sx={{ mb: 1 }}>
        Alchemer webhook setup
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
        Enter these settings in Alchemer so Wrangler can check and count this survey's respondents.
        {config && <> Alchemer survey ID {config.alchemer_survey_id}.</>}
      </Typography>

      {state.loading && <CircularProgress size={24} />}
      {state.error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
          {state.error}
        </Alert>
      )}
      {state.problem && <NotReady surveyId={surveyId} problem={state.problem} onSynced={load} />}

      {config && (
        <>
          <Step number={1} title="Hidden Values">
            {config.hidden_values.map((hidden) => (
              <Box key={hidden.name} sx={{ mb: 1.5 }}>
                <Setting label="Name"><Value copy={hidden.name}>{hidden.name}</Value></Setting>
                <Setting label="Value">
                  {hidden.value ? <Value copy={hidden.value}>{hidden.value}</Value> : <em>empty</em>}
                </Setting>
                <Setting label="Where">{hidden.placement}</Setting>
              </Box>
            ))}
          </Step>

          <Step number={2} title="Custom header (all three webhooks)">
            <Header header={config.header} />
          </Step>

          <Step number={3} title="Check webhook">
            <Webhook webhook={config.check_webhook}>
              <Setting label="Pre-populate">
                field <Value>{config.check_webhook.pre_populate.field_name}</Value> into the question
                {' '}<Value>{config.check_webhook.pre_populate.question}</Value>
              </Setting>
              <Sends
                heading="Send"
                header={config.header}
                fieldsLabel="sid and the quota fields. Enter each variable name exactly as shown; grid rows are listed one per row:"
              >
                <CustomFields fields={config.custom_fields} />
              </Sends>
            </Webhook>
          </Step>

          <Step number={4} title="Survey logic">
            <Setting label="Disqualify">{config.survey_logic.disqualify}</Setting>
            <Setting label="Not quota-checked">{config.survey_logic.not_quota_checked}</Setting>
          </Step>

          <Step number={5} title="Complete webhook">
            <Webhook webhook={config.complete_webhook}>
              <Sends
                heading="Send"
                header={config.header}
                fieldsLabel="sid and the same quota fields as the check webhook (step 3):"
              >
                <CustomFields fields={config.custom_fields} />
              </Sends>
            </Webhook>
          </Step>

          <Step number={6} title="Disqualify webhook">
            <Webhook webhook={config.disqualify_webhook}>
              <Setting label="Why">{config.disqualify_webhook.purpose}</Setting>
              <Sends
                heading="Send, on each of those pages"
                header={config.header}
                fieldsLabel={(
                  <Box component="span" sx={{ display: 'inline-flex', gap: 1, flexWrap: 'wrap' }}>
                    {config.disqualify_webhook.custom_fields.map((name) => <Value key={name} copy={name}>{name}</Value>)}
                  </Box>
                )}
              />
            </Webhook>
          </Step>
        </>
      )}
    </AppShell>
  );
};

export default WranglerSetup;
